import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProductItemResponse } from '@/api/product/types';
import { ActionModal, AlertTypes, Button, Drawer, Icon } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	AccountId,
	SparePartRegistrationResponse,
	FilterUnitRegistrationId,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import { getGetFilterRegistrationQueryKey } from '@/generated/filter-registration';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useDeleteSparePartRegistration, useUpdateSparePartRegistration } from '@/generated/spare-parts';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './SparePart.module.scss';
import { SparePartForm } from './shared/SparePartForm';

interface EditSparePartProps {
	ariaControlId: string;
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	filterUnitId: FilterUnitRegistrationId;
	sparePart?: SparePartRegistrationResponse;
	productItems?: ProductItemResponse[] | null;
	isOpen: boolean;
	drawerRef: React.RefObject<HTMLDivElement>;
	handleClose: () => void;
}

export const EditSparePart: React.FunctionComponent<EditSparePartProps> = ({
	ariaControlId,
	accountId,
	registrationCompositeId,
	filterUnitId,
	sparePart,
	productItems,
	isOpen,
	drawerRef,
	handleClose,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const deleteSparePartMutation = useDeleteSparePartRegistration({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				await queryClient.invalidateQueries({
					queryKey: getGetFilterRegistrationQueryKey(
						segmentationId,
						accountId,
						registrationCompositeId,
						filterUnitId,
					),
				});

				closeModal();
				handleClose();
			},
			onError: () => {
				addNotification({
					children: translations?.data.sparePartRegistrations.deleteSparePart.failed,
					type: AlertTypes.DANGER,
				});
			},
		},
	});

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
	const [hasFormChanged, setHasFormChanged] = useState(false);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

	const formId = 'edit-sparePart-form'; // used for binding form and submit button

	const updateSparePartRegistrationMutation = useUpdateSparePartRegistration({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				// refresh filter unit after update
				await queryClient.invalidateQueries({
					queryKey: getGetFilterRegistrationQueryKey(
						segmentationId,
						accountId,
						registrationCompositeId,
						filterUnitId,
					),
				});

				addNotification({
					children: translations?.data.sparePartRegistrations.saveSuccess,
					type: AlertTypes.SUCCESS,
				});

				handleClose();
			},
			onError: (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
				} else if (isHttpValidationProblemDetails(error)) {
					addNotification({
						children: translations?.data.sparePartRegistrations.saveFailed,
						type: AlertTypes.DANGER,
					});

					setValidationErrors(error.errors);
				} else {
					addNotification({
						children: translations?.data.shared.genericErrorMsg,
						type: AlertTypes.DANGER,
					});
				}
			},
		},
	});

	useEffect(() => {
		setHasFormChanged(false);
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const openModal = useCallback(() => {
		setIsDeleteConfirmOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsDeleteConfirmOpen(false);
	}, []);

	const handleRemoveSparePart = () => {
		if (!sparePart) return;

		deleteSparePartMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			filterUnitId,
			sparePartId: sparePart.id,
		});
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const formData = new FormData(event.target);

		if (!sparePart) return;

		setValidationErrors(null);

		const sparePartForm = {
			articleNumber: String(formData.get('articleNumber')),
			quantity: Number(formData.get('quantity')) || 1,
			saleDate: formData.get('saleDate') ? String(formData.get('saleDate')) : null,
		};

		updateSparePartRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			filterUnitId,
			sparePartId: Number(sparePart.id),
			data: { ...sparePartForm },
		});

		handleClose();
	};

	return (
		<Drawer drawerRef={drawerRef} isOpen={isOpen} id={ariaControlId} desktopWidth={'400px'} direction={'Right'}>
			<Drawer.Header title={translations?.data.sparePartRegistrations.editSparePart} handleClose={handleClose} />

			<Drawer.Body>
				<div className={styles.innerBody}>
					<SparePartForm
						formId={formId}
						legend={translations?.data.sparePartRegistrations.editSparePart}
						sparePart={sparePart}
						productItems={productItems}
						validationErrors={validationErrors}
						handleSubmit={handleSubmit}
						handleChange={handleChange}
					/>

					<Button onClick={openModal} variant={'outline'} isFullWidth className={styles.deleteBtn}>
						{translations?.data.shared.delete}
						<Icon name={'trash'} size={'md'} />
					</Button>

					{isDeleteConfirmOpen && (
						<ActionModal
							size={'sm'}
							translations={translations?.data}
							onSubmit={handleRemoveSparePart}
							onCancel={closeModal}
							headline={translations?.data.sparePartRegistrations.deleteSparePart.headline}
							message={translations?.data.sparePartRegistrations.deleteSparePart.teaser}
						/>
					)}
				</div>
			</Drawer.Body>

			<Drawer.Footer className={styles.footer} hasNoPadding>
				<Button variant={'ghost'} isFullWidth onClick={handleClose}>
					{translations?.data.shared.cancel}
				</Button>
				<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
					{translations?.data.sparePartRegistrations.save}
				</Button>
			</Drawer.Footer>
		</Drawer>
	);
};
